import { useCallback, useRef, useState, useSyncExternalStore } from 'react'
import { BOOK_GLYPH } from '@shared/components/SkillGlyph'
import { kindOf } from '../libraryNode'
import type { Edge, Node } from '@xyflow/react'
import type { CanvasEdge, CanvasNode, CanvasPage } from '@shared/services/canvasService'
import { copyProjectFiles } from '@shared/services/projectFileService'
import { personalProject } from '@shared/services/pictureService'
import {
  loadToolbarWorkflows,
  onToolbarWorkflows,
  putToolbarWorkflow,
  removeToolbarWorkflow,
  toolbarWorkflows,
  type ToolbarWorkflow,
} from '@shared/services/workflowStore'
import type { Draft } from './WorkflowBuilder'
import {
  PAID_TYPES,
  STEP_TYPES,
  WORKFLOW_GLYPH,
  analyse,
  byPlace,
  inOrder,
  markedSteps,
  portOf,
  runLeftovers,
  type WorkflowDef,
  type WorkflowInput,
} from './workflowModel'
import type { WorkflowNodeData } from './workflowNode'

/**
 * Everything a workflow does on the board (workflowModel.ts says what that
 * is): build, merge, run, open, and the toolbar. Kept out of ProjectCanvas,
 * which hands in what it needs (`WorkflowBoard`).
 */
export const WORKFLOW_SKILL_ID = 'canvas-workflow'
const TOOLBAR_PREFIX = 'toolbar-workflow:'
export const isToolbarWorkflow = (skillId: string) => skillId.startsWith(TOOLBAR_PREFIX)

type Fn = (id: string) => Promise<unknown> | void
export interface WorkflowBoard {
  nodes: Node[]
  edges: Edge[]
  latestNodes: { current: Node[] }
  latestEdges: { current: Edge[] }
  setNodes: (change: (prev: Node[]) => Node[]) => void
  setEdges: (change: (prev: Edge[]) => Edge[]) => void
  pages: CanvasPage[]
  setPages: (change: (prev: CanvasPage[]) => CanvasPage[]) => void
  pageId: string
  setPageId: (id: string) => void
  remember: (step: boolean) => void
  newId: (kind: string) => string
  wire: (edge: { id: string; source: string; target: string; targetHandle?: string | null }) => Edge
  sizeOf: (node: Node) => { w: number; h: number }
  freshCopy: (data: Record<string, unknown>) => Record<string, unknown>
  /** The run functions, as they are on the latest render. */
  runs: { current: Record<string, Fn | ((id: string, ...rest: never[]) => unknown)> }
  /** An Edit area card's studio has its result ready to be applied. */
  editReady: (cardId: string) => boolean
  /** Runs going now for this person, and the limit, as the server last said. */
  live: { current: { live: number; limit: number } }
  projectId: string
  ensureProject: () => Promise<string>
  /** Files a board now has (after copying some in). */
  refreshFiles: (projectId: string) => Promise<void>
  thumbOf: (node: Node) => string
  onNote: (text: string) => void
  gap: number
}

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))
/** The page a node is on (the board keeps it beside React Flow's own fields). */
const pg = (node: Node) => (node as { page?: string }).page
const dataOf = (node: Node | undefined) => (node?.data ?? {}) as Record<string, unknown>

/** The card it is, as a step: its name and glyph, from its own data. */
const stepName = (node: Node) => String(dataOf(node).name ?? node.type ?? 'Step')
// An Internal Library card from before 2026-10-22 still carries the grid it
// shared with the Mood board; it is a book now.
const stepGlyph = (node: Node) =>
  node.type === 'library' && kindOf(dataOf(node)) === 'internal' ? BOOK_GLYPH : String(dataOf(node).glyph ?? '◆')

/** Pictures by what they show: a library picture or a project file. */
const pictureOf = (node: Node) => {
  const d = dataOf(node)
  return {
    ...(typeof d.assetId === 'string' ? { assetId: d.assetId } : {}),
    ...(typeof d.fileId === 'string' ? { fileId: d.fileId } : {}),
    ...(typeof d.title === 'string' ? { title: d.title } : {}),
    ...(typeof d.aspect === 'number' ? { aspect: d.aspect } : {}),
    ...(typeof d.rotate === 'number' ? { rotate: d.rotate } : {}),
  }
}
/** Out of whatever frame it was in on the board it came from. */
const withoutFrame = (data: Record<string, unknown>) => {
  const { frame: _frame, ...rest } = data
  return rest
}
const hasPicture = (node: Node | undefined) => {
  const d = dataOf(node)
  return !d.making && (typeof d.fileId === 'string' || typeof d.assetId === 'string')
}

/** The ports a target port takes many wires on; the rest take the first picture. */
const TAKES_MANY = new Set(['grade', 'upscale', 'skill'])

export function useWorkflows(board: WorkflowBoard) {
  const b = useRef(board)
  b.current = board

  /* ---------- the toolbar ---------- */
  const toolbar = useSyncExternalStore(onToolbarWorkflows, toolbarWorkflows, toolbarWorkflows)
  loadToolbarWorkflows()

  /* ---------- building ---------- */
  const [draft, setDraft] = useState<Draft | null>(null)
  /** What the draft was worked out from: the steps, inputs and results. */
  const building = useRef<{ steps: string[]; inputs: string[]; results: string[] } | null>(null)

  const startBuild = useCallback(() => {
    const { nodes, edges, pageId } = b.current
    const chosen = nodes.filter((node) => node.selected && (pg(node) ?? pageId) === pageId)
    const found = analyse(chosen, nodes, edges)
    if (found.steps.length === 0) {
      b.current.onNote(
        found.refused.length > 0
          ? found.refused[0]!.why
          : 'Select the cards of a chain first (Shift + triple-click a card picks all of it), then Build workflow.',
      )
      return
    }
    const stepIds = new Set(found.steps.map((one) => one.id))
    const feeds = (id: string) => {
      const made = new Set(edges.filter((edge) => edge.source === id).map((edge) => edge.target))
      return [...new Set(edges.filter((edge) => made.has(edge.source) && stepIds.has(edge.target)).map((edge) => edge.target))]
    }
    building.current = {
      steps: found.steps.map((one) => one.id),
      inputs: found.inputs.map((one) => one.id),
      results: found.results.map((one) => one.id),
    }
    setDraft({
      name: 'Workflow',
      inputs: found.inputs.map((node, i) => ({
        id: node.id,
        name: String(dataOf(node).title ?? '').replace(/\.[a-z0-9]{2,4}$/i, '').slice(0, 40) || `Picture ${i + 1}`,
        mode: 'replace',
        thumb: b.current.thumbOf(node),
      })),
      steps: found.steps.sort(byPlace).map((node) => ({
        id: node.id,
        name: stepName(node),
        glyph: stepGlyph(node),
        kept: true,
        output: found.terminal.includes(node.id),
        feeds: feeds(node.id),
      })),
      refused: found.refused.map((one) => `${stepName(one.node)} (${one.why})`),
      estimate: found.estimate,
    })
  }, [])

  const cancelBuild = useCallback(() => {
    setDraft(null)
    building.current = null
  }, [])

  /** The chain into one card: its steps onto a page of their own (workflowModel.ts). */
  const merge = useCallback((final: Draft) => {
    const x = b.current
    const { nodes, edges, newId, wire } = x
    const kept = new Set(final.steps.filter((one) => one.kept).map((one) => one.id))
    if (kept.size === 0) return
    const page = newId('wfpage')
    const cardId = newId('workflow')
    const keptSteps = nodes.filter((node) => kept.has(node.id))
    // Everything the kept steps made; what another kept step took goes inside.
    const made = new Set(edges.filter((edge) => kept.has(edge.source)).map((edge) => edge.target))
    const taken = new Set(edges.filter((edge) => kept.has(edge.target)).map((edge) => edge.source))
    const outputSteps = new Set(final.steps.filter((one) => one.kept && one.output).map((one) => one.id))
    // Results that stay on the board, coming out of the card: from the steps
    // whose pictures come out, and not taken by another step.
    const staying = new Set(
      edges
        .filter((edge) => outputSteps.has(edge.source) && !taken.has(edge.target))
        .map((edge) => edge.target),
    )
    const moving = new Set([...kept, ...[...made].filter((id) => !staying.has(id))])

    // The inputs: a copy of each on the workflow's page, its default.
    const inputs: WorkflowInput[] = []
    const copies = new Map<string, string>()
    const copyNodes: Node[] = []
    // Only the inputs a kept step takes.
    for (const one of final.inputs.filter((input) => edges.some((edge) => edge.source === input.id && kept.has(edge.target)))) {
      const original = nodes.find((node) => node.id === one.id)
      if (!original) continue
      const copyId = newId('image')
      copies.set(one.id, copyId)
      copyNodes.push({
        ...original,
        id: copyId,
        selected: false,
        page,
        data: withoutFrame(x.freshCopy(dataOf(original))),
      } as Node)
      inputs.push({ id: copyId, name: one.name.trim() || 'Picture', mode: one.mode })
    }

    // The results stay on the board, and a copy of each stays in the
    // workflow too, wired from its step where it was (Huanrui, 2026-10-16):
    // opened, the workflow ends in its pictures, and can be carried on from.
    const resultCopies = new Map<string, string>()
    for (const id of staying) {
      const original = nodes.find((node) => node.id === id)
      if (!original || original.type !== 'image') continue
      const copyId = newId('image')
      resultCopies.set(id, copyId)
      copyNodes.push({
        ...original,
        id: copyId,
        selected: false,
        page,
        data: withoutFrame(x.freshCopy(dataOf(original))),
      } as Node)
    }

    const top = Math.min(...keptSteps.map((node) => node.position.y))
    const left = Math.min(...keptSteps.map((node) => node.position.x))
    const def: WorkflowDef = {
      name: final.name.trim() || 'Workflow',
      glyph: WORKFLOW_GLYPH,
      inputs,
      outputs: [...outputSteps],
      steps: kept.size,
      estimate: final.estimate,
      builtAt: new Date().toISOString(),
      locate: 'share',
    }
    const card: Node = {
      id: cardId,
      type: 'workflow',
      page: x.pageId,
      position: { x: left, y: top },
      selected: true,
      data: {
        skillRef: WORKFLOW_SKILL_ID,
        name: 'Workflow',
        glyph: WORKFLOW_GLYPH,
        glow: '#9a8cff',
        open: true,
        workflow: def,
        wfPage: page,
      } satisfies WorkflowNodeData,
    } as Node

    x.remember(true)
    x.setPages((prev) => [...prev, { id: page, name: def.name, workflow: cardId } as CanvasPage])
    x.setNodes((prev) => [
      ...prev.map((node) =>
        moving.has(node.id)
          ? ({ ...node, page, selected: false, data: withoutFrame(dataOf(node)) } as Node)
          : node.selected
            ? { ...node, selected: false }
            : node,
      ),
      ...copyNodes,
      card,
    ])
    x.setEdges((prev) => {
      const out: Edge[] = []
      for (const edge of prev) {
        // An input into a kept step: from its copy on the workflow's page.
        if (kept.has(edge.target) && copies.has(edge.source)) {
          out.push({ ...edge, source: copies.get(edge.source)! })
          continue
        }
        // A result that stays: now out of the card -- and its copy in the
        // workflow, out of the step, as it was.
        if (kept.has(edge.source) && staying.has(edge.target)) {
          const copy = resultCopies.get(edge.target)
          if (copy) out.push({ ...edge, id: newId('edge'), target: copy })
          continue
        }
        out.push(edge)
      }
      // Each input that changes, wired into its port, as it was into the chain.
      for (const one of inputs) {
        const original = [...copies.entries()].find(([, copy]) => copy === one.id)?.[0]
        if (one.mode === 'replace' && original) {
          out.push(wire({ id: newId('edge'), source: original, target: cardId, targetHandle: portOf(one.id) }))
        }
      }
      for (const id of staying) out.push(wire({ id: newId('edge'), source: cardId, target: id }))
      return out
    })
    setDraft(null)
    building.current = null
    x.onNote(`"${def.name}" is one card now. Open it to see its steps.`)
  }, [])

  /* ---------- open and back ---------- */
  const returnTo = useRef('')
  const openWorkflow = useCallback((cardId: string) => {
    const card = b.current.nodes.find((node) => node.id === cardId)
    const page = String(dataOf(card).wfPage ?? '')
    if (!page || !b.current.pages.some((one) => one.id === page)) {
      b.current.onNote('This workflow has lost its steps')
      return
    }
    // A workflow merged before 2026-10-16 left no picture after the steps
    // whose pictures come out: the first results beside the card -- those of
    // the merge -- are copied in after them, one a step, so it can be carried on.
    const x = b.current
    const def = (card?.data as WorkflowNodeData | undefined)?.workflow
    if (card && def) {
      const leftover = runLeftovers(x.nodes, x.edges, page)
      const here = new Map(x.nodes.filter((node) => pg(node) === page && !leftover.has(node.id)).map((node) => [node.id, node]))
      const bare = def.outputs.filter(
        (stepId) => here.has(stepId) && !x.edges.some((edge) => edge.source === stepId && here.get(edge.target)?.type === 'image'),
      )
      const results = x.edges
        .filter((edge) => edge.source === card.id)
        .map((edge) => x.nodes.find((node) => node.id === edge.target))
        .filter((node): node is Node => Boolean(node) && node!.type === 'image')
      const added: Node[] = []
      const wires: Edge[] = []
      bare.forEach((stepId, i) => {
        const result = results[i]
        const step = here.get(stepId)
        if (!result || !step) return
        const box = x.sizeOf(step)
        const id = x.newId('image')
        added.push({
          ...result,
          id,
          selected: false,
          page,
          position: { x: step.position.x + box.w + 120, y: step.position.y },
          data: withoutFrame(x.freshCopy(dataOf(result))),
        } as Node)
        wires.push(x.wire({ id: x.newId('edge'), source: stepId, target: id }))
      })
      if (added.length > 0) {
        x.setNodes((prev) => [...prev, ...added])
        x.setEdges((prev) => [...prev, ...wires])
      }
    }
    returnTo.current = x.pageId
    x.setPageId(page)
  }, [])

  /** Back from a workflow's page: the workflow worked out again from what is on it. */
  const closeWorkflow = useCallback(() => {
    const x = b.current
    const page = x.pages.find((one) => one.id === x.pageId)
    const cardId = (page as { workflow?: string } | undefined)?.workflow
    const card = cardId ? x.nodes.find((node) => node.id === cardId) : undefined
    const back = returnTo.current && x.pages.some((one) => one.id === returnTo.current) ? returnTo.current : ((card ? pg(card) : undefined) ?? x.pages[0]?.id ?? '')
    if (card && page) {
      const d = card.data as WorkflowNodeData
      const leftover = runLeftovers(x.nodes, x.edges, page.id)
      const template = x.nodes.filter((node) => pg(node) === page.id && !leftover.has(node.id))
      const found = analyse(template.filter((node) => STEP_TYPES.has(node.type ?? '')), x.nodes, x.edges)
      const before = new Map(d.workflow.inputs.map((one) => [one.id, one]))
      const inputs: WorkflowInput[] = found.inputs
        .filter((node) => pg(node) === page.id)
        .map((node, i) => before.get(node.id) ?? { id: node.id, name: String(dataOf(node).title ?? '').slice(0, 40) || `Picture ${i + 1}`, mode: 'replace' as const })
      // Kept in their order; any new one after them.
      inputs.sort((p, q) => {
        const a = d.workflow.inputs.findIndex((one) => one.id === p.id)
        const c = d.workflow.inputs.findIndex((one) => one.id === q.id)
        return (a < 0 ? 999 : a) - (c < 0 ? 999 : c)
      })
      const stepIds = new Set(found.steps.map((one) => one.id))
      // A step whose pictures came out, carried on from since: what comes out
      // now is the end of the chain after it (2026-10-16).
      const terminal = new Set(found.terminal)
      const after = (from: string): string[] => {
        const seen = new Set<string>([from])
        const queue = [from]
        const ends: string[] = []
        while (queue.length > 0) {
          const at = queue.shift()!
          const products = x.edges.filter((edge) => edge.source === at).map((edge) => edge.target)
          const next = x.edges
            .filter((edge) => products.includes(edge.source) && stepIds.has(edge.target))
            .map((edge) => edge.target)
          for (const one of next) {
            if (seen.has(one)) continue
            seen.add(one)
            if (terminal.has(one)) ends.push(one)
            queue.push(one)
          }
        }
        return ends
      }
      const outputs = [
        ...new Set(
          d.workflow.outputs
            .filter((id) => stepIds.has(id))
            .flatMap((id) => (terminal.has(id) ? [id] : after(id).length > 0 ? after(id) : [id])),
        ),
      ]
      const def: WorkflowDef = {
        ...d.workflow,
        inputs,
        outputs: outputs.length > 0 ? outputs : found.terminal,
        steps: found.steps.length,
        estimate: d.workflow.estimate,
      }
      const ports = new Set(inputs.filter((one) => one.mode === 'replace').map((one) => portOf(one.id)))
      x.setNodes((prev) => prev.map((node) => (node.id === card.id ? { ...node, data: { ...node.data, workflow: def } } : node)))
      // A port that went: its wires go too.
      x.setEdges((prev) => prev.filter((edge) => edge.target !== card.id || ports.has(String(edge.targetHandle ?? ''))))
      x.setPages((prev) => prev.map((one) => (one.id === page.id ? { ...one, name: def.name } : one)))
    }
    returnTo.current = ''
    if (back) x.setPageId(back)
  }, [])

  /* ---------- running ---------- */
  /** Paid steps going now from workflows on this board. */
  const paying = useRef(0)
  const takeTurn = useCallback(async () => {
    for (;;) {
      const { live, limit } = b.current.live.current
      const most = limit || 5
      if (paying.current < most && live < most) {
        paying.current += 1
        return
      }
      await sleep(1500)
    }
  }, [])
  const giveTurn = useCallback(() => {
    paying.current = Math.max(0, paying.current - 1)
  }, [])

  /** Wait until the board has drawn what was just put on it. */
  const settle = useCallback(async (ready: () => boolean) => {
    for (let i = 0; i < 200 && !ready(); i += 1) await sleep(50)
    await sleep(60)
  }, [])

  /**
   * One step, run as its card's own Run does, and the pictures it made.
   * Refused for being over the limit: tried again a little later.
   */
  const runStep = useCallback(
    async (cardId: string, type: string): Promise<string[]> => {
      const x = b.current
      const fn: Record<string, string> = {
        picture: 'runPictures',
        upscale: 'runUpscale',
        video: 'runVideo',
        angle: 'runAngle',
        vector: 'runVector',
        grade: 'runGrade',
        edit: 'runEdit',
        skill: 'run',
      }
      const name = fn[type]
      if (!name) throw new Error(`A ${type} card cannot be run in a workflow`)
      const card = () => x.latestNodes.current.find((node) => node.id === cardId)
      const busy = () => {
        const d = dataOf(card())
        return Boolean(d.running || d.preparing || d.applying || d.pending || (Array.isArray(d.runs) && d.runs.length > 0))
      }
      const longest = type === 'video' ? 45 * 60_000 : 20 * 60_000
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const before = new Set(x.latestEdges.current.filter((edge) => edge.source === cardId).map((edge) => edge.target))
        const made = () =>
          x.latestEdges.current
            .filter((edge) => edge.source === cardId && !before.has(edge.target))
            .map((edge) => x.latestNodes.current.find((node) => node.id === edge.target))
            .filter((node): node is Node => Boolean(node) && node!.type === 'image')
        // Clear an earlier refusal so it is not read as this one's.
        x.setNodes((prev) => prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, error: '' } } : node)))
        await settle(() => !dataOf(card()).error)
        await (b.current.runs.current[name] as Fn)(cardId)
        const began = Date.now()
        let refused = false
        // Edit area: its result is brushed and applied before it is a picture.
        if (type === 'edit') {
          for (;;) {
            const d = dataOf(card())
            if (typeof d.error === 'string' && d.error) {
              if (/already going|wait for one/i.test(d.error)) {
                refused = true
                break
              }
              throw new Error(d.error)
            }
            if (d.blend && !busy() && b.current.editReady(cardId)) break
            if (Date.now() - began > longest) throw new Error('Edit area took too long')
            await sleep(500)
          }
          if (!refused) await (b.current.runs.current.applyBlend as Fn)(cardId)
        }
        if (!refused) {
          for (;;) {
            const d = dataOf(card())
            if (typeof d.error === 'string' && d.error) {
              if (/already going|wait for one|at once|limit/i.test(d.error)) {
                refused = true
                break
              }
              throw new Error(d.error)
            }
            const out = made()
            if (out.length > 0 && !busy() && out.every(hasPicture)) return out.map((node) => node.id)
            // Nothing started, nothing said: refused quietly for the limit.
            if (out.length === 0 && !busy() && Date.now() - began > 8000) {
              refused = true
              break
            }
            if (Date.now() - began > longest) throw new Error('A step took too long')
            await sleep(600)
          }
        }
        await sleep(4000 + attempt * 3000)
      }
      throw new Error('It could not start: too many running. Try again in a moment.')
    },
    [settle],
  )

  const runWorkflow = useCallback(
    async (cardId: string) => {
      const x = b.current
      const card = x.nodes.find((node) => node.id === cardId)
      if (!card) return
      const d = card.data as WorkflowNodeData
      if (d.running) return
      const def = d.workflow
      const page = d.wfPage
      const patchCard = (change: Partial<WorkflowNodeData>) =>
        x.setNodes((prev) => prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, ...change } } : node)))
      // What earlier runs left on the page goes first: the page holds the
      // workflow and nothing else (Huanrui, 2026-10-16).
      const before = runLeftovers(x.nodes, x.edges, page)
      if (before.size > 0) {
        x.setNodes((prev) => prev.filter((node) => !before.has(node.id)))
        x.setEdges((prev) => prev.filter((edge) => !before.has(edge.source) && !before.has(edge.target)))
      }
      const template = x.nodes.filter((node) => pg(node) === page && !before.has(node.id))
      const steps = template.filter((node) => STEP_TYPES.has(node.type ?? ''))
      if (steps.length === 0) return patchCard({ error: 'This workflow has no steps' })
      const templateIds = new Set(template.map((node) => node.id))
      const templateEdges = x.edges.filter((edge) => templateIds.has(edge.source) && templateIds.has(edge.target))

      // What goes into each input, run by run.
      const wiredInto = (input: WorkflowInput) =>
        x.edges
          .filter((edge) => edge.target === cardId && edge.targetHandle === portOf(input.id))
          .map((edge) => x.nodes.find((node) => node.id === edge.source))
          .filter((node): node is Node => Boolean(node) && hasPicture(node))
          .sort(byPlace)
      const given = new Map<string, Node[]>()
      for (const input of def.inputs) {
        if (input.mode !== 'replace') continue
        given.set(input.id, wiredInto(input))
      }
      const counts = [...given.values()].map((list) => list.length)
      if (counts.some((n) => n === 0)) return patchCard({ error: 'Wire a picture into every input' })
      const total = counts.length === 0 ? 1 : Math.max(...counts)
      if (counts.some((n) => n !== 1 && n !== total)) return patchCard({ error: 'Give each input the same number of pictures, or one' })

      const id = x.projectId || (await x.ensureProject())
      if (!id) return patchCard({ error: 'Save the board first' })

      // Room on the workflow's page below everything already there, a row a run.
      const onPage = template
      const box = (node: Node) => x.sizeOf(node)
      const tTop = Math.min(...template.map((node) => node.position.y))
      const tBottom = Math.max(...template.map((node) => node.position.y + box(node).h))
      const rowH = tBottom - tTop + 220
      const floor = Math.max(...onPage.map((node) => node.position.y + box(node).h)) + 260

      patchCard({ running: true, error: '', progress: `0 of ${total} done` })
      let done = 0
      let failed = 0
      let lastError = ''
      const report = () =>
        patchCard({ progress: `${done} of ${total} done${failed ? ` · ${failed} failed` : ''}${paying.current ? ` · ${paying.current} paid step${paying.current === 1 ? '' : 's'} going` : ''}` })
      const order = inOrder(steps, templateEdges)
      // What comes out: the steps chosen when it was built, and any step one
      // of whose pictures is marked, wherever it is in the chain; in the
      // order the steps run.
      const marked = markedSteps(template, templateEdges)
      const coming = new Set([...def.outputs, ...marked])
      const outputSteps = order.filter((step) => coming.has(step.id)).map((step) => step.id)
      const outputs: { node: Node; run: number; at: number }[] = []

      const one = async (run: number) => {
        const key = x.newId('wfrun')
        const dy = floor + run * rowH - tTop
        const clones = new Map<string, string>()
        const added: Node[] = []
        for (const step of steps) {
          const cloneId = x.newId(step.type ?? 'skill')
          clones.set(step.id, cloneId)
          added.push({
            ...step,
            id: cloneId,
            selected: false,
            page,
            position: { x: step.position.x, y: step.position.y + dy },
            data: { ...x.freshCopy(dataOf(step)), open: true, wfRun: key },
          } as Node)
        }
        // Each input's picture for this run, where its default stands.
        const wires: Edge[] = []
        for (const input of def.inputs) {
          const slot = template.find((node) => node.id === input.id)
          if (!slot) continue
          const list = given.get(input.id)
          const picture = input.mode === 'replace' && list ? (list.length === 1 ? list[0]! : list[run]!) : slot
          const pictureId = x.newId('image')
          added.push({
            ...slot,
            id: pictureId,
            selected: false,
            page,
            position: { x: slot.position.x, y: slot.position.y + dy },
            data: { ...pictureOf(picture), wfRun: key },
          } as Node)
          for (const edge of templateEdges.filter((one) => one.source === slot.id && clones.has(one.target))) {
            wires.push(x.wire({ id: x.newId('edge'), source: pictureId, target: clones.get(edge.target)!, targetHandle: edge.targetHandle }))
          }
        }
        x.setNodes((prev) => [...prev, ...added])
        x.setEdges((prev) => [...prev, ...wires])
        await settle(() => added.every((node) => x.latestNodes.current.some((one) => one.id === node.id)))

        const madeBy = new Map<string, string[]>()
        for (const step of order) {
          const clone = clones.get(step.id)!
          const paid = PAID_TYPES.has(step.type ?? '')
          if (paid) await takeTurn()
          report()
          let made: string[]
          try {
            made = await runStep(clone, step.type ?? '')
          } finally {
            if (paid) giveTurn()
          }
          madeBy.set(step.id, made)
          // On to the steps that took this one's picture, at the same ports.
          const passed = templateEdges.filter((edge) => edge.source === step.id).map((edge) => edge.target)
          const next: Edge[] = []
          for (const edge of templateEdges.filter((one) => passed.includes(one.source) && clones.has(one.target))) {
            const target = steps.find((s) => s.id === edge.target)
            const take = TAKES_MANY.has(target?.type ?? '') ? made : made.slice(0, 1)
            for (const picture of take) {
              next.push(x.wire({ id: x.newId('edge'), source: picture, target: clones.get(edge.target)!, targetHandle: edge.targetHandle }))
            }
          }
          if (next.length > 0) {
            x.setEdges((prev) => [...prev, ...next])
            await settle(() => next.every((edge) => x.latestEdges.current.some((one) => one.id === edge.id)))
          }
        }
        outputSteps.forEach((stepId, at) => {
          for (const pictureId of madeBy.get(stepId) ?? []) {
            const node = x.latestNodes.current.find((one) => one.id === pictureId)
            if (node) outputs.push({ node, run, at })
          }
        })
      }

      // Every run at once; the paid steps among them take turns.
      await Promise.all(
        Array.from({ length: total }, (_, run) =>
          one(run)
            .then(() => {
              done += 1
            })
            .catch((error: unknown) => {
              failed += 1
              lastError = error instanceof Error ? error.message : 'A step failed'
            })
            .finally(report),
        ),
      )

      // The results to the right of the card, under any earlier ones.
      const now = b.current
      const self = now.latestNodes.current.find((node) => node.id === cardId)
      if (self && outputs.length > 0) {
        const size = now.sizeOf(self)
        const earlier = now.latestEdges.current
          .filter((edge) => edge.source === cardId)
          .map((edge) => now.latestNodes.current.find((node) => node.id === edge.target))
          .filter((node): node is Node => Boolean(node))
        let y = earlier.length > 0 ? Math.max(...earlier.map((node) => node.position.y + now.sizeOf(node).h)) + 40 : self.position.y
        const placed: Node[] = []
        const wires: Edge[] = []
        outputs.sort((p, q) => p.run - q.run || p.at - q.at)
        for (const { node } of outputs) {
          const w = 220
          const aspect = Number(dataOf(node).aspect) || now.sizeOf(node).w / Math.max(1, now.sizeOf(node).h) || 1
          const outId = now.newId('image')
          placed.push({
            id: outId,
            type: 'image',
            page: pg(self),
            position: { x: self.position.x + size.w + now.gap, y },
            width: w,
            data: {
              ...pictureOf(node),
              ...(dataOf(node).from ? { from: dataOf(node).from } : {}),
              // Never marked, even when it came out because it was marked in
              // the workflow (Huanrui, 2026-10-16): the mark says what comes
              // out, and stays in the workflow.
              aspect,
            },
          } as Node)
          wires.push(now.wire({ id: now.newId('edge'), source: cardId, target: outId }))
          y += Math.round(w / aspect) + 40
        }
        now.setNodes((prev) => [...prev, ...placed])
        now.setEdges((prev) => [...prev, ...wires])
      }
      // The runs' copies go once their pictures are out: the workflow's page
      // keeps the workflow only. The results beside the card are their own.
      const after = runLeftovers(now.latestNodes.current, now.latestEdges.current, page)
      if (after.size > 0) {
        now.setNodes((prev) => prev.filter((node) => !after.has(node.id)))
        now.setEdges((prev) => prev.filter((edge) => !after.has(edge.source) && !after.has(edge.target)))
      }
      patchCard({
        running: false,
        progress: '',
        error: failed === 0 ? '' : failed === total ? lastError : `${failed} of ${total} failed: ${lastError}`,
      })
    },
    [giveTurn, runStep, settle, takeTurn],
  )

  /* ---------- the toolbar ---------- */
  /** The workflow's page as a copy: its steps and pictures, from its corner. */
  const snapshot = useCallback(
    async (cardId: string): Promise<Omit<ToolbarWorkflow, 'id' | 'savedAt'> | null> => {
      const x = b.current
      const card = x.nodes.find((node) => node.id === cardId)
      if (!card) return null
      const d = card.data as WorkflowNodeData
      const leftover = runLeftovers(x.nodes, x.edges, String(d.wfPage ?? ''))
      const template = x.nodes.filter((node) => pg(node) === d.wfPage && !leftover.has(node.id))
      const ids = new Set(template.map((node) => node.id))
      const left = Math.min(...template.map((node) => node.position.x))
      const top = Math.min(...template.map((node) => node.position.y))
      const personal = await personalProject()
      const fileIds = [...new Set(template.map((node) => dataOf(node).fileId).filter((one): one is string => typeof one === 'string'))]
      const moved = fileIds.length > 0 ? await copyProjectFiles(personal, fileIds) : {}
      const nodes: CanvasNode[] = template.map((node) => {
        const own = { ...x.freshCopy(dataOf(node)) }
        if (typeof own.fileId === 'string' && moved[own.fileId]) own.fileId = moved[own.fileId]
        return {
          id: node.id,
          type: node.type as CanvasNode['type'],
          position: { x: node.position.x - left, y: node.position.y - top },
          ...(node.width ? { width: node.width } : {}),
          ...(node.height ? { height: node.height } : {}),
          data: own,
        }
      })
      const edges: CanvasEdge[] = x.edges
        .filter((edge) => ids.has(edge.source) && ids.has(edge.target))
        .map((edge) => ({ id: edge.id, source: edge.source, target: edge.target, ...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}) }))
      return { name: d.workflow.name, glyph: d.workflow.glyph, def: d.workflow as unknown as Record<string, unknown>, doc: { nodes, edges }, project: personal }
    },
    [],
  )

  const toolbarAction = useCallback(
    async (cardId: string, how: 'add' | 'update' | 'remove') => {
      const x = b.current
      const card = x.nodes.find((node) => node.id === cardId)
      if (!card) return
      const d = card.data as WorkflowNodeData
      const setToolbarId = (toolbarId: string | undefined) =>
        x.setNodes((prev) =>
          prev.map((node) => {
            if (node.id !== cardId) return node
            const workflow = { ...(node.data as WorkflowNodeData).workflow }
            if (toolbarId) workflow.toolbarId = toolbarId
            else delete workflow.toolbarId
            return { ...node, data: { ...node.data, workflow } }
          }),
        )
      if (how === 'remove') {
        if (d.workflow.toolbarId) removeToolbarWorkflow(d.workflow.toolbarId)
        setToolbarId(undefined)
        x.onNote(`"${d.workflow.name}" is no longer in your toolbar. This card stays.`)
        return
      }
      try {
        const copy = await snapshot(cardId)
        if (!copy) return
        const id = how === 'update' && d.workflow.toolbarId ? d.workflow.toolbarId : x.newId('wf')
        putToolbarWorkflow({ ...copy, id, savedAt: new Date().toISOString(), def: { ...copy.def, toolbarId: id } })
        setToolbarId(id)
        x.onNote(how === 'update' ? `Your toolbar's "${copy.name}" is now this one.` : `"${copy.name}" is in your toolbar, under the cards. Changing it here does not change that copy.`)
      } catch (error) {
        x.onNote(`It could not be added: ${error instanceof Error ? error.message : 'the pictures did not copy'}`)
      }
    },
    [snapshot],
  )

  /** A workflow from the toolbar, put down on this board. */
  const placeFromToolbar = useCallback(async (skillId: string, at: { x: number; y: number }) => {
    const x = b.current
    const entry = toolbarWorkflows().find((one) => `${TOOLBAR_PREFIX}${one.id}` === skillId)
    if (!entry) return
    try {
      const projectId = x.projectId || (await x.ensureProject())
      const fileIds = [...new Set(entry.doc.nodes.map((node) => node.data.fileId).filter((one): one is string => typeof one === 'string'))]
      const moved = fileIds.length > 0 && entry.project !== projectId ? await copyProjectFiles(projectId, fileIds) : {}
      if (fileIds.length > 0) await x.refreshFiles(projectId)
      const page = x.newId('wfpage')
      const cardId = x.newId('workflow')
      const remap = new Map(entry.doc.nodes.map((node) => [node.id, x.newId(node.type)]))
      const nodes: Node[] = entry.doc.nodes.map((node) => {
        const own = { ...node.data }
        if (typeof own.fileId === 'string' && moved[own.fileId]) own.fileId = moved[own.fileId]
        if (typeof own.frame === 'string') delete own.frame
        return { ...node, id: remap.get(node.id)!, page, position: { x: node.position.x + 80, y: node.position.y + 80 }, data: own } as Node
      })
      const edges: Edge[] = entry.doc.edges
        .filter((edge) => remap.has(edge.source) && remap.has(edge.target))
        .map((edge) => x.wire({ id: x.newId('edge'), source: remap.get(edge.source)!, target: remap.get(edge.target)!, targetHandle: edge.targetHandle }))
      const old = entry.def as unknown as WorkflowDef
      const def: WorkflowDef = {
        ...old,
        inputs: old.inputs.map((one) => ({ ...one, id: remap.get(one.id) ?? one.id })),
        outputs: old.outputs.map((id) => remap.get(id) ?? id),
        toolbarId: entry.id,
      }
      x.remember(true)
      x.setPages((prev) => [...prev, { id: page, name: def.name, workflow: cardId } as CanvasPage])
      x.setNodes((prev) => [
        ...prev.map((node) => (node.selected ? { ...node, selected: false } : node)),
        ...nodes,
        {
          id: cardId,
          type: 'workflow',
          page: x.pageId,
          position: at,
          selected: true,
          data: { skillRef: WORKFLOW_SKILL_ID, name: 'Workflow', glyph: def.glyph, glow: '#9a8cff', open: true, workflow: def, wfPage: page } satisfies WorkflowNodeData,
        } as Node,
      ])
      x.setEdges((prev) => [...prev, ...edges])
    } catch (error) {
      x.onNote(`The workflow could not be put down: ${error instanceof Error ? error.message : 'its pictures did not copy'}`)
    }
  }, [])

  return {
    toolbar,
    toolbarPrefix: TOOLBAR_PREFIX,
    draft,
    setDraft,
    building,
    startBuild,
    cancelBuild,
    merge,
    openWorkflow,
    closeWorkflow,
    runWorkflow,
    toolbarAction,
    placeFromToolbar,
  }
}
